// Минималистичный, но настоящий SVG-чарт: сглаженная линия (catmull-rom→bezier),
// y-grid с подписями, понятные оси, hover-tooltip через <title> и CSS-увеличение точки.

export interface ChartPoint { label: string; value: number; }
export interface ChartOpts {
  color?: string;
  height?: number;
  format?: (v: number) => string;
  unit?: string;
  smooth?: boolean;
}

let _gradId = 0;

export function lineChart(data: ChartPoint[], opts: ChartOpts = {}): string {
  if (!data.length) return `<div class="empty">Нет данных</div>`;

  const w = 800;
  const h = opts.height ?? 260;
  const padL = 48, padR = 16, padT = 16, padB = 32;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const color = opts.color ?? "#6ea8ff";
  const fmt = opts.format ?? (v => String(Math.round(v * 100) / 100));
  const unit = opts.unit ?? "";
  const smooth = opts.smooth !== false;

  const vals = data.map(d => d.value);
  const rawMax = Math.max(...vals, 1);
  const rawMin = Math.min(...vals, 0);
  // округляем верх до приятного значения
  const max = niceMax(rawMax);
  const min = Math.min(rawMin, 0);
  const range = max - min || 1;
  const step = innerW / Math.max(data.length - 1, 1);

  const xs = data.map((_, i) => padL + i * step);
  const ys = data.map(d => padT + innerH - ((d.value - min) / range) * innerH);

  const linePath = smooth ? catmullRomPath(xs, ys) : `M ${xs.map((x, i) => `${x},${ys[i]}`).join(" L ")}`;
  const baselineY = padT + innerH;
  const areaPath = `${linePath} L ${xs[xs.length - 1]},${baselineY} L ${xs[0]},${baselineY} Z`;

  const gid = `cg-${++_gradId}`;
  const gridLines = 4;
  const grid = Array.from({ length: gridLines + 1 }, (_, i) => {
    const y = padT + (innerH * i) / gridLines;
    const v = max - (range * i) / gridLines;
    return `
      <line x1="${padL}" y1="${y}" x2="${padL + innerW}" y2="${y}"
            stroke="rgba(255,255,255,0.04)" stroke-width="1"/>
      <text x="${padL - 8}" y="${y + 3.5}" text-anchor="end"
            fill="#6e7079" font-size="10" font-family="-apple-system, system-ui, sans-serif">${fmt(v)}</text>`;
  }).join("");

  // прореженные X-подписи (макс 7 штук, чтобы не сливалось)
  const maxXLabels = 7;
  const skipX = Math.max(1, Math.ceil(data.length / maxXLabels));
  const xLabels = data.map((d, i) =>
    i % skipX === 0 || i === data.length - 1
      ? `<text x="${xs[i]}" y="${h - 10}" text-anchor="middle"
              fill="#6e7079" font-size="10"
              font-family="-apple-system, system-ui, sans-serif">${escapeXml(d.label)}</text>`
      : ""
  ).join("");

  const dots = data.map((d, i) => `
    <g class="dot-g">
      <circle class="dot" cx="${xs[i]}" cy="${ys[i]}" r="3" fill="${color}"
              stroke="#0b0d12" stroke-width="2"/>
      <title>${escapeXml(d.label)}: ${fmt(d.value)}${unit ? " " + escapeXml(unit) : ""}</title>
    </g>`).join("");

  return `
    <div class="chart-wrap">
      <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" class="chart-svg" role="img">
        <defs>
          <linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="${color}" stop-opacity="0.32"/>
            <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
          </linearGradient>
        </defs>
        ${grid}
        <path d="${areaPath}" fill="url(#${gid})"/>
        <path d="${linePath}" stroke="${color}" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        ${dots}
        ${xLabels}
      </svg>
    </div>
  `;
}

// Catmull-Rom → cubic Bézier (alpha = 0.5, центрипетальный)
function catmullRomPath(xs: number[], ys: number[]): string {
  if (xs.length < 2) return "";
  const segs: string[] = [`M ${xs[0]},${ys[0]}`];
  for (let i = 0; i < xs.length - 1; i++) {
    const p0x = xs[i - 1] ?? xs[i],     p0y = ys[i - 1] ?? ys[i];
    const p1x = xs[i],                  p1y = ys[i];
    const p2x = xs[i + 1],              p2y = ys[i + 1];
    const p3x = xs[i + 2] ?? xs[i + 1], p3y = ys[i + 2] ?? ys[i + 1];
    const c1x = p1x + (p2x - p0x) / 6;
    const c1y = p1y + (p2y - p0y) / 6;
    const c2x = p2x - (p3x - p1x) / 6;
    const c2y = p2y - (p3y - p1y) / 6;
    segs.push(`C ${c1x},${c1y} ${c2x},${c2y} ${p2x},${p2y}`);
  }
  return segs.join(" ");
}

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / exp;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * exp;
}

function escapeXml(s: string): string {
  return String(s).replace(/[&<>"']/g, c => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!
  ));
}

// ===== Bar chart =====
export function barChart(data: ChartPoint[], opts: ChartOpts = {}): string {
  if (!data.length) return `<div class="empty">Нет данных</div>`;
  const w = 800, h = opts.height ?? 220;
  const padL = 48, padR = 16, padT = 16, padB = 32;
  const innerW = w - padL - padR, innerH = h - padT - padB;
  const color = opts.color ?? "#a78bfa";
  const fmt = opts.format ?? (v => String(Math.round(v * 100) / 100));
  const unit = opts.unit ?? "";
  const max = niceMax(Math.max(...data.map(d => d.value), 1));
  const bw = (innerW / data.length) * 0.7;
  const gap = (innerW / data.length) * 0.3;
  const gid = `bg-${++_gradId}`;

  const grid = Array.from({ length: 5 }, (_, i) => {
    const y = padT + (innerH * i) / 4;
    const v = max - (max * i) / 4;
    return `
      <line x1="${padL}" y1="${y}" x2="${padL + innerW}" y2="${y}"
            stroke="rgba(255,255,255,0.04)" stroke-width="1"/>
      <text x="${padL - 8}" y="${y + 3.5}" text-anchor="end" fill="#6e7079" font-size="10">${fmt(v)}</text>`;
  }).join("");

  const bars = data.map((d, i) => {
    const x = padL + i * (bw + gap) + gap / 2;
    const bh = (d.value / max) * innerH;
    const y = padT + innerH - bh;
    return `
      <g>
        <rect x="${x}" y="${y}" width="${bw}" height="${bh}" rx="4" fill="url(#${gid})"/>
        <title>${escapeXml(d.label)}: ${fmt(d.value)}${unit ? " " + escapeXml(unit) : ""}</title>
        <text x="${x + bw / 2}" y="${h - 10}" text-anchor="middle" fill="#6e7079" font-size="10">${escapeXml(d.label)}</text>
      </g>`;
  }).join("");

  return `
    <div class="chart-wrap">
      <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" class="chart-svg" role="img">
        <defs>
          <linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="${color}" stop-opacity="1"/>
            <stop offset="100%" stop-color="${color}" stop-opacity="0.4"/>
          </linearGradient>
        </defs>
        ${grid}
        ${bars}
      </svg>
    </div>`;
}
